import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../../supabase";
import { useAuth } from "../../contexts/AuthContext";
import "./AuthPage.css";

function AuthPage() {
  const navigate = useNavigate();
  const { continueAsGuest } = useAuth();
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");

  async function handleSubmit() {
    setError("");
    setSuccess("");

    if (mode === "forgot") {
      if (!email.trim()) return setError("Email is required.");
      setLoading(true);
      try {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin,
        });
        if (error) throw error;
        setSuccess(
          "If that email is registered, a password reset link is on its way. Check your inbox.",
        );
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!email.trim() || !password.trim())
      return setError("Email and password are required.");
    if (mode === "signup" && !username.trim())
      return setError("Username is required.");

    setLoading(true);
    try {
      if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        navigate("/app");
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { username },
            emailRedirectTo: `${window.location.origin}/app`,
          },
        });
        if (error) throw error;
        setSuccess(
          "Account created! Check your email and click the confirmation link — you'll be signed in automatically.",
        );
        setMode("signin");
        setEmail("");
        setPassword("");
        setUsername("");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <Link to="/" className="auth-logo-link">
          <div className="auth-logo">
            <span className="auth-logo-text">TOURNAMENT CITY</span>
          </div>
        </Link>

        <h2 className="auth-title">
          {mode === "signin"
            ? "Sign In"
            : mode === "signup"
              ? "Create Account"
              : "Reset Password"}
        </h2>

        {mode === "forgot" && (
          <p className="auth-hint">
            Enter your account email and we'll send you a link to set a new
            password.
          </p>
        )}

        {mode === "signup" && (
          <div className="auth-field">
            <label className="auth-label">Username</label>
            <input
              className="auth-input"
              type="text"
              placeholder="Choose a username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
        )}

        <div className="auth-field">
          <label className="auth-label">Email</label>
          <input
            className="auth-input"
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) =>
              e.key === "Enter" && mode === "forgot" && handleSubmit()
            }
          />
        </div>

        {mode !== "forgot" && (
          <div className="auth-field">
            <div className="auth-label-row">
              <label className="auth-label">Password</label>
              {mode === "signin" && (
                <button
                  className="auth-link auth-link-inline"
                  onClick={() => {
                    setMode("forgot");
                    setError("");
                    setSuccess("");
                  }}
                >
                  Forgot password?
                </button>
              )}
            </div>
            <input
              className="auth-input"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            />
          </div>
        )}

        {error && <p className="auth-error">{error}</p>}
        {success && <p className="auth-success">{success}</p>}

        <button className="auth-btn" onClick={handleSubmit} disabled={loading}>
          {loading
            ? "Please wait..."
            : mode === "signin"
              ? "Sign In"
              : mode === "signup"
                ? "Create Account"
                : "Send reset link"}
        </button>

        {/* ── Guest mode ── */}
        {mode !== "forgot" && (
          <>
            <div className="auth-divider">
              <span>or</span>
            </div>

            <button
              className="auth-btn-guest"
              onClick={() => {
                continueAsGuest();
                navigate("/app");
              }}
            >
              Continue as Guest
            </button>
          </>
        )}

        <p className="auth-switch">
          {mode === "signin" ? (
            <>
              Don't have an account?{" "}
              <button
                className="auth-link"
                onClick={() => {
                  setMode("signup");
                  setError("");
                  setSuccess("");
                }}
              >
                Create one
              </button>
            </>
          ) : mode === "signup" ? (
            <>
              Already have an account?{" "}
              <button
                className="auth-link"
                onClick={() => {
                  setMode("signin");
                  setError("");
                  setSuccess("");
                }}
              >
                Sign in
              </button>
            </>
          ) : (
            <button
              className="auth-link"
              onClick={() => {
                setMode("signin");
                setError("");
                setSuccess("");
              }}
            >
              ← Back to sign in
            </button>
          )}
        </p>

        <Link to="/" className="auth-back">
          ← Back to home
        </Link>
      </div>
    </div>
  );
}

export default AuthPage;
